// frontend/src/components/client/FuelRequirementCalculator.jsx

import React, { useState, useEffect, useCallback } from 'react';
import {
  Calculator, AlertTriangle, CheckCircle, Users, Moon, Cloud,
  Navigation, Fuel as FuelIcon, ArrowRight, MapPin, Wind,
  Info, MapPinOff, Eye, EyeOff, Shield
} from 'lucide-react';
import useDistanceCalculator from '../../hooks/useDistanceCalculator';

// ─────────────────────────────────────────────────────────────────────────────
// Sub-component : IFR Alternate Panel
// ─────────────────────────────────────────────────────────────────────────────
const IFRAlternatePanel = ({
  requestId,
  destinationIcao,
  alternateIcao, setAlternateIcao,
  alternateDistanceNm, setAlternateDistanceNm,
  ceilingFt, setCeilingFt,
  visibilitySm, setVisibilitySm,
}) => {
  const [resolving, setResolving] = useState(false);
  const [resolveStatus, setResolveStatus] = useState(null); // 'ok' | 'manual' | null
  const [show123, setShow123] = useState(false);

  // Règle 1-2-3 locale (affichage seulement — le backend fait foi)
  const exempt123 = ceilingFt >= 2000 && visibilitySm >= 3.0;

  const resolveAlternateDistance = useCallback(async () => {
    if (!alternateIcao || alternateIcao.length < 3) return;
    setResolving(true);
    setResolveStatus(null);
    try {
      const token = localStorage.getItem('client_access_token');
      const res = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/client/fuel/${requestId}/alternate-distance?alternate_icao=${alternateIcao.toUpperCase()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      if (data.computed && data.distance_nm) {
        setAlternateDistanceNm(parseFloat(data.distance_nm.toFixed(1)));
        setResolveStatus('ok');
      } else {
        setResolveStatus('manual');
      }
    } catch {
      setResolveStatus('manual');
    } finally {
      setResolving(false);
    }
  }, [alternateIcao, requestId, setAlternateDistanceNm]);

  return (
    <div className="space-y-3 p-3 bg-blue-500/5 border border-blue-500/20 rounded-xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Shield className="w-4 h-4 text-blue-400" />
          <span className="text-xs font-semibold text-blue-300">IFR Requirements — §91.169</span>
        </div>
        <button
          onClick={() => setShow123(!show123)}
          className="text-xs text-gray-500 hover:text-gray-300 flex items-center gap-1 transition"
        >
          {show123 ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
          Rule 1-2-3
        </button>
      </div>

      {/* Règle 1-2-3 §91.169(c)(1) */}
      {show123 && (
        <div className={`rounded-lg p-3 border text-xs space-y-2 ${
          exempt123
            ? 'bg-green-500/10 border-green-500/30'
            : 'bg-yellow-500/10 border-yellow-500/30'
        }`}>
          <div className="flex items-center gap-2">
            <Info className="w-3.5 h-3.5 text-yellow-400 shrink-0" />
            <p className="text-gray-300 font-medium">Alternate Exemption — §91.169(c)(1)</p>
          </div>
          <p className="text-gray-400">
            No alternate required if, within <strong className="text-white">±1 hour of ETA</strong>,
            forecast ceiling ≥ 2,000 ft AGL <strong className="text-white">AND</strong> visibility ≥ 3 SM.
          </p>
          <div className="grid grid-cols-2 gap-2 mt-2">
            <div>
              <label className="block text-gray-500 mb-1">Forecast Ceiling (ft AGL)</label>
              <input
                type="number"
                value={ceilingFt ?? ''}
                onChange={e => setCeilingFt(e.target.value ? parseInt(e.target.value) : null)}
                placeholder="e.g. 3000"
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-2 py-1.5 text-white text-xs"
              />
            </div>
            <div>
              <label className="block text-gray-500 mb-1">Forecast Visibility (SM)</label>
              <input
                type="number"
                step="0.5"
                value={visibilitySm ?? ''}
                onChange={e => setVisibilitySm(e.target.value ? parseFloat(e.target.value) : null)}
                placeholder="e.g. 5"
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-2 py-1.5 text-white text-xs"
              />
            </div>
          </div>
          {ceilingFt !== null && visibilitySm !== null && (
            <div className={`flex items-center gap-2 mt-1 ${exempt123 ? 'text-green-400' : 'text-yellow-400'}`}>
              {exempt123
                ? <><CheckCircle className="w-3.5 h-3.5" /> Alternate NOT required (1-2-3 rule met)</>
                : <><AlertTriangle className="w-3.5 h-3.5" /> Alternate REQUIRED (1-2-3 rule not met)</>
              }
            </div>
          )}
        </div>
      )}

      {/* Alternate ICAO */}
      <div>
        <label className="block text-xs text-gray-400 mb-1 flex items-center gap-1">
          <MapPinOff className="w-3 h-3 text-orange-400" />
          Alternate Airport ICAO
          <span className="text-gray-600 ml-1">§91.169(b)</span>
        </label>
        <div className="flex gap-2">
          <input
            type="text"
            value={alternateIcao}
            onChange={e => setAlternateIcao(e.target.value.toUpperCase())}
            onBlur={resolveAlternateDistance}
            placeholder="e.g. KDAL"
            maxLength={4}
            className="flex-1 bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm font-mono placeholder:text-gray-600 uppercase"
          />
          <button
            onClick={resolveAlternateDistance}
            disabled={resolving || !alternateIcao}
            className="px-3 py-2 bg-blue-500/20 hover:bg-blue-500/30 rounded-lg text-blue-400 text-xs transition disabled:opacity-40"
          >
            {resolving ? (
              <div className="w-3.5 h-3.5 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
            ) : (
              <Navigation className="w-3.5 h-3.5" />
            )}
          </button>
        </div>
        {resolveStatus === 'ok' && (
          <p className="text-xs text-green-400 mt-1 flex items-center gap-1">
            <CheckCircle className="w-3 h-3" />
            Distance resolved from database
          </p>
        )}
        {resolveStatus === 'manual' && (
          <p className="text-xs text-yellow-400 mt-1 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" />
            Airport not in DB — enter distance manually below
          </p>
        )}
      </div>

      {/* Alternate Distance */}
      <div>
        <label className="block text-xs text-gray-400 mb-1 flex items-center gap-1">
          <ArrowRight className="w-3 h-3" />
          {destinationIcao || 'Dest'} → Alternate Distance (NM)
        </label>
        <div className="relative">
          <input
            type="number"
            value={alternateDistanceNm ?? ''}
            onChange={e => setAlternateDistanceNm(e.target.value ? parseFloat(e.target.value) : null)}
            placeholder="Auto-calculated or enter manually"
            min="0"
            step="1"
            className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm pr-10"
          />
          {alternateDistanceNm && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500">NM</span>
          )}
        </div>
        {!alternateDistanceNm && (
          <p className="text-xs text-gray-500 mt-1">
            ⚠️ If blank, backend uses 45-min cruise fallback (~{Math.round(140 * 0.75)} NM est.)
          </p>
        )}
      </div>

      {/* Holding info */}
      <div className="flex items-start gap-2 bg-gray-900/40 rounded-lg p-2">
        <Info className="w-3.5 h-3.5 text-gray-500 mt-0.5 shrink-0" />
        <p className="text-xs text-gray-500">
          Holding fuel: <strong className="text-gray-400">45 min</strong> at normal cruise speed,
          calculated after reaching alternate. §91.169(b)(1)
        </p>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────
const FuelRequirementCalculator = ({
  aircraft,
  requestId,
  departureIcao,
  destinationIcao,
  onCalculationComplete
}) => {
  const { calculateDistance } = useDistanceCalculator();

  const [tripDistance, setTripDistance]         = useState('');
  const [autoDistance, setAutoDistance]         = useState(null);
  const [useAutoDistance, setUseAutoDistance]   = useState(true);
  const [passengerCount, setPassengerCount]     = useState(1);
  const [baggageWeight, setBaggageWeight]       = useState(0);
  const [isNightFlight, setIsNightFlight]       = useState(false);
  const [isIFR, setIsIFR]                       = useState(false);
  const [taxiFuel, setTaxiFuel]                 = useState(2);
  const [cruiseAltitude, setCruiseAltitude]     = useState(8000);

  // IFR Alternate
  const [alternateIcao, setAlternateIcao]           = useState('');
  const [alternateDistanceNm, setAlternateDistanceNm] = useState(null);
  const [ceilingFt, setCeilingFt]                   = useState(null);
  const [visibilitySm, setVisibilitySm]             = useState(null);

  const [calculating, setCalculating]               = useState(false);
  const [calculatingWithWind, setCalculatingWithWind] = useState(false);
  const [result, setResult]                         = useState(null);
  const [windResult, setWindResult]                 = useState(null);
  const [error, setError]                           = useState(null);
  const [distanceInfo, setDistanceInfo]             = useState(null);

  // ── Auto-distance via hook ────────────────────────────────────────────────
  useEffect(() => {
    if (departureIcao && destinationIcao) {
      const dist = calculateDistance(departureIcao, destinationIcao);
      if (dist.distanceNM) {
        setAutoDistance(dist.distanceNM);
        setDistanceInfo(dist);
        if (useAutoDistance) setTripDistance(dist.distanceNM.toString());
      } else {
        setAutoDistance(null);
        setDistanceInfo(null);
      }
    } else {
      setAutoDistance(null);
      setDistanceInfo(null);
    }
  }, [departureIcao, destinationIcao, calculateDistance, useAutoDistance]);

  // ── Build payload ─────────────────────────────────────────────────────────
  const buildPayload = (distance) => ({
    aircraft_id: aircraft.id,
    trip_distance_nm: distance,
    passenger_count: passengerCount,
    baggage_weight_lbs: baggageWeight,
    is_night_flight: isNightFlight,
    is_ifr: isIFR,
    taxi_fuel_gallons: taxiFuel,
    // IFR fields — envoyés même si VFR (backend ignore si is_ifr=false)
    alternate_icao: alternateIcao || null,
    alternate_distance_nm: alternateDistanceNm || null,
    destination_ceiling_ft: ceilingFt ?? null,
    destination_visibility_sm: visibilitySm ?? null,
  });

  const resolveDistance = () => {
    if (useAutoDistance && autoDistance) {
      setTripDistance(autoDistance.toString());
      return autoDistance;
    }
    return parseFloat(tripDistance);
  };

  // ── Calculate (no wind) ───────────────────────────────────────────────────
  const handleCalculate = async () => {
    const distance = resolveDistance();
    if (!distance || distance <= 0) { setError('Please enter a valid trip distance'); return; }
    setCalculating(true);
    setError(null);
    try {
      const token = localStorage.getItem('client_access_token');
      const res = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/client/fuel/${requestId}/calculate-fuel`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(buildPayload(distance))
        }
      );
      if (!res.ok) throw new Error('Calculation failed');
      const data = await res.json();
      setResult(data);
      setWindResult(null);
      if (onCalculationComplete) onCalculationComplete(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setCalculating(false);
    }
  };

  // ── Calculate with wind (NOAA) ────────────────────────────────────────────
  const handleCalculateWithWind = async () => {
    const distance = resolveDistance();
    if (!distance || distance <= 0) { setError('Please enter a valid trip distance'); return; }
    setCalculatingWithWind(true);
    setError(null);
    try {
      const token = localStorage.getItem('client_access_token');
      const res = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/client/fuel/${requestId}/calculate-fuel-with-wind`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ ...buildPayload(distance), cruise_altitude_ft: cruiseAltitude })
        }
      );
      if (!res.ok) throw new Error('Wind calculation failed');
      const data = await res.json();
      setWindResult(data);
      setResult(data);
      if (onCalculationComplete) onCalculationComplete(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setCalculatingWithWind(false);
    }
  };

  const formatGal  = (v) => `${v} gal`;
  const formatHrs  = (v) => `${v} hrs`;

  const activeResult = windResult || result;

  const reserveLabel = () => {
    if (isIFR) return 'IFR: 45 min reserve + alternate + 45 min holding (§91.169)';
    if (isNightFlight) return 'Night VFR: 45 min reserve (§91.151)';
    return 'Day VFR: 30 min reserve (§91.151)';
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-3">
        <Calculator className="w-5 h-5 text-blue-400" />
        <h4 className="text-white font-semibold">FAA Fuel Requirements Calculator</h4>
      </div>

      {/* Route display */}
      {(departureIcao || destinationIcao) && (
        <div className="bg-gray-800/30 rounded-lg p-3 border border-gray-700">
          <div className="flex items-center justify-between">
            <div className="text-center flex-1">
              <p className="text-xs text-gray-500">Departure</p>
              <div className="flex items-center justify-center gap-1 mt-1">
                <MapPin className="w-3 h-3 text-green-400" />
                <span className="text-white font-mono font-bold text-sm">{departureIcao || '—'}</span>
              </div>
              {distanceInfo?.departure?.city && (
                <p className="text-xs text-gray-500 mt-0.5">{distanceInfo.departure.city}</p>
              )}
            </div>
            <div className="text-center flex-0 px-2">
              <ArrowRight className="w-4 h-4 text-gray-500" />
              {distanceInfo?.distanceNM && (
                <p className="text-xs text-blue-400 mt-0.5">{distanceInfo.distanceNM} NM</p>
              )}
            </div>
            <div className="text-center flex-1">
              <p className="text-xs text-gray-500">Destination</p>
              <div className="flex items-center justify-center gap-1 mt-1">
                <MapPin className="w-3 h-3 text-orange-400" />
                <span className="text-white font-mono font-bold text-sm">{destinationIcao || '—'}</span>
              </div>
              {distanceInfo?.destination?.city && (
                <p className="text-xs text-gray-500 mt-0.5">{distanceInfo.destination.city}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Inputs */}
      <div className="space-y-3">
        {/* Trip Distance */}
        <div>
          <label className="block text-xs text-gray-400 mb-1 flex items-center gap-1">
            <Navigation className="w-3 h-3" />
            Trip Distance (NM)
          </label>
          {autoDistance && (
            <label className="flex items-center gap-2 mb-1.5 cursor-pointer">
              <input
                type="checkbox"
                checked={useAutoDistance}
                onChange={e => {
                  setUseAutoDistance(e.target.checked);
                  if (e.target.checked && autoDistance) setTripDistance(autoDistance.toString());
                }}
                className="rounded border-gray-600"
              />
              <span className="text-xs text-blue-400">
                Use calculated distance: {autoDistance} NM
              </span>
            </label>
          )}
          <input
            type="number"
            value={tripDistance}
            onChange={e => { setTripDistance(e.target.value); setUseAutoDistance(false); }}
            disabled={useAutoDistance && !!autoDistance}
            placeholder="Enter distance in NM"
            min="1"
            step="1"
            className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm disabled:opacity-50"
          />
        </div>

        {/* Passengers + Baggage */}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-xs text-gray-400 mb-1 flex items-center gap-1">
              <Users className="w-3 h-3" />
              Passengers (incl. pilot)
            </label>
            <input
              type="number"
              value={passengerCount}
              onChange={e => setPassengerCount(parseInt(e.target.value) || 1)}
              min="1"
              max={aircraft?.seats || 20}
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm"
            />
            <p className="text-xs text-gray-600 mt-0.5">190 lbs/pax (FAA std)</p>
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Baggage (lbs)</label>
            <input
              type="number"
              value={baggageWeight}
              onChange={e => setBaggageWeight(parseFloat(e.target.value) || 0)}
              min="0"
              step="10"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm"
            />
          </div>
        </div>

        {/* Night / IFR toggles */}
        <div className="grid grid-cols-2 gap-2">
          <label className="flex items-center gap-2 p-2 bg-gray-900/50 rounded-lg cursor-pointer">
            <input
              type="checkbox"
              checked={isNightFlight}
              onChange={e => setIsNightFlight(e.target.checked)}
              className="rounded border-gray-600"
            />
            <Moon className="w-3.5 h-3.5 text-gray-400" />
            <span className="text-xs text-gray-300">Night Flight</span>
          </label>
          <label className="flex items-center gap-2 p-2 bg-gray-900/50 rounded-lg cursor-pointer">
            <input
              type="checkbox"
              checked={isIFR}
              onChange={e => setIsIFR(e.target.checked)}
              className="rounded border-gray-600"
            />
            <Cloud className="w-3.5 h-3.5 text-gray-400" />
            <span className="text-xs text-gray-300">IFR Flight</span>
          </label>
        </div>

        {/* IFR Alternate Panel — affiché seulement si IFR */}
        {isIFR && (
          <IFRAlternatePanel
            requestId={requestId}
            destinationIcao={destinationIcao}
            alternateIcao={alternateIcao}
            setAlternateIcao={setAlternateIcao}
            alternateDistanceNm={alternateDistanceNm}
            setAlternateDistanceNm={setAlternateDistanceNm}
            ceilingFt={ceilingFt}
            setCeilingFt={setCeilingFt}
            visibilitySm={visibilitySm}
            setVisibilitySm={setVisibilitySm}
          />
        )}

        {/* Taxi Fuel */}
        <div>
          <label className="block text-xs text-gray-400 mb-1 flex items-center gap-1">
            <FuelIcon className="w-3 h-3" />
            Taxi Fuel (gallons)
          </label>
          <input
            type="number"
            value={taxiFuel}
            onChange={e => setTaxiFuel(parseFloat(e.target.value) || 0)}
            min="0"
            step="0.5"
            className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm"
          />
        </div>

        {/* Cruise Altitude (pour calcul vent) */}
        <div>
          <label className="block text-xs text-gray-400 mb-1 flex items-center gap-1">
            <Wind className="w-3 h-3" />
            Cruise Altitude (ft) — for NOAA winds
          </label>
          <select
            value={cruiseAltitude}
            onChange={e => setCruiseAltitude(parseInt(e.target.value))}
            className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm"
          >
            {[3000, 4000, 5000, 6000, 7000, 8000, 9000, 10000, 12000, 14000, 18000, 24000, 30000, 34000, 39000].map(alt => (
              <option key={alt} value={alt}>{alt.toLocaleString()} ft</option>
            ))}
          </select>
        </div>

        {/* Buttons */}
        <div className="flex gap-2">
          <button
            onClick={handleCalculate}
            disabled={calculating}
            className="flex-1 py-2.5 bg-gray-600 hover:bg-gray-500 rounded-lg text-white font-medium text-sm disabled:opacity-50 transition"
          >
            {calculating ? 'Calculating...' : 'Calculate (No Wind)'}
          </button>
          <button
            onClick={handleCalculateWithWind}
            disabled={calculatingWithWind}
            className="flex-1 py-2.5 bg-blue-500 hover:bg-blue-600 rounded-lg text-white font-medium text-sm disabled:opacity-50 transition flex items-center justify-center gap-2"
          >
            {calculatingWithWind ? (
              <><div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />Fetching NOAA...</>
            ) : (
              <><Wind className="w-3.5 h-3.5" />Calculate with Wind</>
            )}
          </button>
        </div>

        {error && (
          <div className="flex items-center gap-2 p-2 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-xs">
            <AlertTriangle className="w-3.5 h-3.5" />
            {error}
          </div>
        )}
      </div>

      {/* ── Results ────────────────────────────────────────────────────────── */}
      {activeResult && (
        <div className="mt-4 p-3 bg-gray-900/50 rounded-lg border border-gray-700 space-y-2">

          {/* Regulation header */}
          <div className="flex items-center justify-between border-b border-gray-700 pb-2">
            <span className="text-xs text-gray-400">Regulation</span>
            <span className="text-xs text-cyan-400">{reserveLabel()}</span>
          </div>

          {/* Exemption 1-2-3 badge */}
          {activeResult.alternate_exemption_123 && (
            <div className="flex items-center gap-2 bg-green-500/10 border border-green-500/30 rounded-lg px-2 py-1.5">
              <CheckCircle className="w-3.5 h-3.5 text-green-400" />
              <span className="text-xs text-green-400">Alternate exempt — §91.169(c)(1) rule 1-2-3 met</span>
            </div>
          )}

          {/* Alternate info */}
          {activeResult.alternate_required && activeResult.alternate_icao_used && (
            <div className="flex items-center gap-2 text-xs text-gray-400">
              <MapPinOff className="w-3 h-3 text-orange-400" />
              Alternate: <span className="font-mono text-white">{activeResult.alternate_icao_used}</span>
              {activeResult.alternate_distance_used_nm && (
                <span className="text-gray-500">({activeResult.alternate_distance_used_nm} NM)</span>
              )}
            </div>
          )}

          {/* Wind info */}
          {windResult && windResult.wind_source !== 'none' && (
            <div className="bg-blue-500/10 rounded-lg p-2 border border-blue-500/30">
              <div className="flex items-center gap-2 mb-1">
                <Wind className="w-3.5 h-3.5 text-blue-400" />
                <span className="text-xs text-blue-400 font-medium">NOAA Winds Aloft</span>
                <span className="text-xs text-gray-500 ml-auto">{windResult.wind_source}</span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div><span className="text-gray-500">Headwind:</span><span className="text-white ml-1">{windResult.wind_component_kts} kts</span></div>
                <div><span className="text-gray-500">Groundspeed:</span><span className="text-white ml-1">{windResult.effective_groundspeed_kts} kts</span></div>
                <div><span className="text-gray-500">Trip time:</span><span className="text-white ml-1">{formatHrs(windResult.adjusted_trip_time_hours)}</span></div>
                <div><span className="text-gray-500">No-wind trip:</span><span className="text-white ml-1">{formatGal(windResult.trip_fuel_gallons_no_wind)}</span></div>
              </div>
            </div>
          )}

          {/* Fuel breakdown */}
          <div className="grid grid-cols-2 gap-2 pt-1">
            <div>
              <p className="text-xs text-gray-500">Trip Fuel</p>
              <p className="text-white font-semibold">
                {formatGal(windResult ? windResult.trip_fuel_gallons_with_wind : activeResult.trip_fuel_gallons)}
              </p>
              {windResult && (
                <p className="text-xs text-gray-600">no wind: {formatGal(windResult.trip_fuel_gallons_no_wind)}</p>
              )}
            </div>
            <div>
              <p className="text-xs text-gray-500">
                Reserve ({activeResult.legal_reserve_minutes} min)
              </p>
              <p className="text-yellow-400 font-semibold">{formatGal(activeResult.reserve_fuel_gallons)}</p>
            </div>
            {activeResult.alternate_fuel_gallons > 0 && (
              <div>
                <p className="text-xs text-gray-500">
                  Alternate {activeResult.alternate_distance_used_nm
                    ? `(${activeResult.alternate_distance_used_nm} NM)`
                    : '(est.)'}
                </p>
                <p className="text-orange-400 font-semibold">{formatGal(activeResult.alternate_fuel_gallons)}</p>
              </div>
            )}
            {activeResult.holding_fuel_gallons > 0 && (
              <div>
                <p className="text-xs text-gray-500">Holding ({activeResult.holding_minutes} min)</p>
                <p className="text-purple-400 font-semibold">{formatGal(activeResult.holding_fuel_gallons)}</p>
              </div>
            )}
            {activeResult.weight_penalty_gallons > 0 && (
              <div>
                <p className="text-xs text-gray-500">Weight adj.</p>
                <p className="text-gray-400 text-sm">{formatGal(activeResult.weight_penalty_gallons)}</p>
              </div>
            )}
            <div className="col-span-2 pt-1 border-t border-gray-700">
              <p className="text-xs text-gray-500">Total Required</p>
              <p className="text-green-400 font-bold text-lg">{formatGal(activeResult.total_fuel_required_gallons)}</p>
            </div>
          </div>

          {/* Performance */}
          <div className="pt-2 border-t border-gray-700">
            <p className="text-xs text-gray-500">Aircraft Performance</p>
            <div className="flex justify-between mt-1">
              <span className="text-xs text-gray-400">Burn: {activeResult.fuel_burn_rate_gph} GPH</span>
              <span className="text-xs text-gray-400">Endurance: {formatHrs(activeResult.estimated_endurance_hours)}</span>
            </div>
          </div>

          {/* Notes */}
          {activeResult.notes && (
            <p className="text-xs text-gray-500 italic pt-1">{activeResult.notes}</p>
          )}

          {/* Disclaimer */}
          <div className="flex items-start gap-2 pt-2 border-t border-gray-700 mt-2">
            {windResult?.wind_source === 'NOAA_AWC' ? (
              <>
                <CheckCircle className="w-3.5 h-3.5 text-green-400 mt-0.5" />
                <div>
                  <span className="text-green-400 text-xs">NOAA winds aloft applied</span>
                  <p className="text-gray-500 text-xs mt-1">Planning estimate — obtain official weather briefing and file via Leidos (1800wxbrief.com).</p>
                </div>
              </>
            ) : windResult?.wind_source === 'none' ? (
              <>
                <AlertTriangle className="w-3.5 h-3.5 text-yellow-400 mt-0.5" />
                <div>
                  <span className="text-yellow-400 text-xs">⚠️ No NOAA data — 15% margin on trip fuel applied</span>
                  <p className="text-gray-500 text-xs mt-1">Try "Calculate with Wind" for accurate wind-adjusted fuel.</p>
                </div>
              </>
            ) : (
              <>
                <AlertTriangle className="w-3.5 h-3.5 text-yellow-400 mt-0.5" />
                <div>
                  <span className="text-yellow-400 text-xs">⚠️ No wind adjustment applied</span>
                  <p className="text-gray-500 text-xs mt-1">Use "Calculate with Wind" for FAA-compliant wind-adjusted fuel planning.</p>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FuelRequirementCalculator;